<template>
  <div>
    <div
      v-if="isGuest"
      class="alert alert-info"
    >
      If you are a returning user, <a href="/login">please log into your account</a> to place your order. The following
      form is for NEW users only.
    </div>

    <h2>About You</h2>

    <p>
      Answer a few questions to set up your account
    </p>

    <div class="mt-4"></div>

    <ValidationObserver
      ref="validator"
      slim
    >
      <div class="mb-4">
        <ValidationWrapper
          :field="formData.partner_building_id"
          class="vForm-field"
        >
          <label class="form-label">
            {{ formData.partner_building_id.getLabel() }}
          </label>

          <ControlSelect
            v-model="formData.partner_building_id.value"
            :enhanced-mode="true"
            :options="formData.partner_building_id.items"
            :name="formData.partner_building_id.name"
            :placeholder="formData.partner_building_id.placeholder"
          />
        </ValidationWrapper>

        <div class="row">
          <div class="col-12 col-md-4">
            <ValidationWrapper
              :field="formData.dormitory"
              class="vForm-field"
            >
              <label class="form-label">
                {{ formData.dormitory.getLabel() }}
              </label>

              <ControlInput
                v-model="formData.dormitory.value"
                :name="formData.dormitory.name"
              />
            </ValidationWrapper>
          </div>
          <div class="col-12 col-md-4">
            <ValidationWrapper
              :field="formData.house"
              class="vForm-field"
            >
              <label class="form-label">
                {{ formData.house.getLabel() }}
              </label>

              <ControlInput
                v-model="formData.house.value"
                :name="formData.house.name"
              />
            </ValidationWrapper>
          </div>
          <div class="col-12 col-md-4">
            <ValidationWrapper
              :field="formData.room"
              class="vForm-field"
            >
              <label class="form-label">
                {{ formData.room.getLabel() }}
              </label>

              <ControlInput
                v-model="formData.room.value"
                :name="formData.room.name"
              />
            </ValidationWrapper>
          </div>
        </div>
      </div>

      <div class="mb-4">
        <div
          v-show="isGuest"
        >
          <ValidationWrapper
            :field="formData.first_name"
            class="vForm-field"
          >
            <label class="form-label">
              {{ formData.first_name.getLabel() }}
            </label>

            <ControlInput
              v-model="formData.first_name.value"
              :name="formData.first_name.name"
            />
          </ValidationWrapper>
        </div>

        <div
          v-show="isGuest"
        >
          <ValidationWrapper
            :field="formData.last_name"
            class="vForm-field"
          >
            <label class="form-label">
              {{ formData.last_name.getLabel() }}
            </label>

            <ControlInput
              v-model="formData.last_name.value"
              :name="formData.last_name.name"
            />
          </ValidationWrapper>
        </div>

        <ValidationWrapper
          :field="formData.student_id"
          class="vForm-field"
        >
          <label class="form-label">
            {{ formData.student_id.getLabel() }}
          </label>

          <ControlInput
            v-model="formData.student_id.value"
            :name="formData.student_id.name"
          />
        </ValidationWrapper>

        <ValidationWrapper
          :field="formData.phone"
          class="vForm-field"
        >
          <label class="form-label">
            {{ formData.phone.getLabel() }}
          </label>

          <ControlInput
            v-model="formData.phone.value"
            :name="formData.phone.name"
          />
        </ValidationWrapper>
      </div>

      <div class="mb-4">
        <ValidationWrapper
          :field="formData.student_email"
          class="vForm-field"
        >
          <label class="form-label">
            {{ formData.student_email.getLabel() }}
          </label>

          <AppInfo
            :text="userEmailHint"
          />

          <ControlInput
            v-model="formData.student_email.value"
            :name="formData.student_email.name"
          />
        </ValidationWrapper>

        <div
          v-show="isGuest"
        >
          <ValidationWrapper
            :field="formData.account_email"
            class="vForm-field"
          >
            <label class="form-label">
              {{ formData.account_email.getLabel() }}
            </label>

            <AppInfo
              :text="userEmailHint"
            />

            <ControlInput
              v-model="formData.account_email.value"
              :name="formData.account_email.name"
            />
          </ValidationWrapper>
        </div>

        <div
          v-show="isGuest"
        >
          <ValidationWrapper
            :field="formData.password"
            class="vForm-field"
          >
            <label class="form-label">
              {{ formData.password.getLabel() }}
            </label>

            <ControlPassword
              v-model="formData.password.value"
              :name="formData.password.name"
            />
          </ValidationWrapper>
        </div>

        <div
          v-show="isGuest"
        >
          <ValidationWrapper
            :field="formData.password_confirmation"
            class="vForm-field"
          >
            <label class="form-label">
              {{ formData.password_confirmation.getLabel() }}
            </label>

            <ControlPassword
              v-model="formData.password_confirmation.value"
              :name="formData.password_confirmation.name"
            />
          </ValidationWrapper>
        </div>
      </div>

      <div
        v-if="$store.state.collegeStorage.featureFlags.useFullFieldset"
        class="mt-4 mb-4"
      >
        <ValidationWrapper
          :field="formData.special_status"
          class="vForm-field"
        >
          <label class="form-label">
            {{ formData.special_status.getLabel() }}
          </label>

          <ControlSelect
            v-model="formData.special_status.value"
            :placeholder="formData.special_status.placeholder"
            :enhanced-mode="true"
            :options="formData.special_status.items"
            :name="formData.special_status.name"
          />
        </ValidationWrapper>
      </div>

      <div
        v-if="$store.state.collegeStorage.featureFlags.useFullFieldset"
        class="mt-4 mb-4"
      >
        <ValidationWrapper
          :field="formData.moving_off_campus"
          class="vForm-field"
        >
          <ControlCheckbox
            v-model="formData.moving_off_campus.value"
            :label="formData.moving_off_campus.placeholder"
            :name="formData.moving_off_campus.name"
          />
        </ValidationWrapper>
      </div>
    </ValidationObserver>

    <div
      v-show="validationErrorMessage"
      class="border border-danger rounded-3 bg-danger text-white p-4"
    >
      {{ validationErrorMessage }}
    </div>

    <CollegeStorageNavigation
      :show-back-button="false"
      :next-button-options="nextButtonOptions"
      :next-label="nextButtonMessage"
      @back-button-clicked="back"
      @next-button-clicked="submit"
    />
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import Component from "vue-class-component";

import ControlInput from "@app-components/controls/control-input.vue";
import ControlPassword from "@app-components/controls/control-password.vue";
import ControlSelect from "@app-components/controls/control-select.vue";
import ControlCheckbox from "@app-components/controls/control-checkbox.vue";
import AppInfo from "@app-components/app-info.vue"

import CollegeStorageNavigation from "js/UI/CollegeStorage/Navigation.vue";
import ValidationWrapper from "@/UI/CollegeStorage/ValidationWrapper.vue";
import {UserFormData} from "@/UI/stores/college-storage";
import axios from "axios";
import * as _ from "lodash";

@Component({
  components: {
    ValidationWrapper,
    CollegeStorageNavigation,
    ControlInput,
    ControlSelect,
    ControlPassword,
    ControlCheckbox,
    AppInfo
  }
})
export default class CollegeStorage_AboutYou extends Vue {
  formData: UserFormData = this.$store.state.collegeStorage.userFormData;
  validationErrorMessage: string | null = null;
  hasValidationError: boolean = false;

  isBusy = false;

  $refs!: {
    validator: any
  };

  get isGuest() {
    return !this.$store.state.collegeStorage.is_authenticated;
  }

  get userEmailHint() {
    return "We will use your student email address to communicate with the student throughout the process and coordinate delivery efforts with the university. Please use the, &quot;Account Email&quot; if you would like this information ALSO sent to a secondary email address and to use as the main credential for account access. Both are mandatory fields, but feel free to use only a student email address for both if you choose.";
  }

  get nextButtonMessage() {
    return this.isBusy ? "Validating..." : "Next";
  }

  get nextButtonOptions() {
    return {
      disabled: this.isBusy
    }
  }

  created() {
  }

  mounted() {
  }

  back() {
    this.$emit('back-button-clicked');
  }

  next() {
    this.$emit('next-button-clicked');
  }

  submit() {
    let self = this;

    this.$refs.validator.validate()
      .then((isValid) => {
        self.validationErrorMessage = null;

        if (!isValid) {
          self.validationErrorMessage = 'Please check the fields above, some of them are incorrect';

          return;
        }

        this.isBusy = true;

        if (this.isGuest) {
          axios.post('/uchicago/order/user-data-validation', {
            email: this.formData.account_email.value
          })
            .then((response) => {
              self.$emit('next-button-clicked');
            })
            .catch((error) => {
              let givenErrors = _.get(error.response.data.errors, "email");

              if (givenErrors) {
                self.$refs.validator.setErrors({
                  [this.formData.account_email.name as string]: givenErrors
                });

                self.validationErrorMessage = _.flatten(givenErrors).join("; ");
              }

              throw error;
            })
            .finally(() => {
              self.isBusy = false;
            });
        } else {
          self.$emit('next-button-clicked');
        }
      });
  }
}
</script>

<style lang="scss">
.form-label {
  font-weight: bold;
}

.vForm-field {
  margin-bottom: 1rem;
}

.alert {
  a {
    color: #008fc4;
  }
}
</style>
